import { z } from "incur";
import pkg from "../../../package.json" with { type: "json" };
import { detectChannel } from "../build-info";
import {
	isNewerVersion,
	readUpdateCache,
	upgradeCommandFor,
	writeUpdateCache,
} from "./check";
import {
	assetForThisBinary,
	downloadAndSwap,
	fetchManifest,
	SelfUpdateError,
} from "./self-update";

export const upgradeOptions = z.object({
	check: z
		.boolean()
		.optional()
		.describe("Only check for a newer version, don't install it"),
	background: z
		.boolean()
		.optional()
		.describe(
			"Internal: refresh the update cache silently (spawned by the automatic check)",
		),
});

export const upgradeOutput = z.object({
	status: z
		.enum(["up-to-date", "update-available", "updated"])
		.describe("Result of the upgrade check"),
	current: z.string().describe("The version currently running"),
	latest: z.string().describe("The latest released version"),
	channel: z
		.enum(["npm", "brew", "standalone", "dev"])
		.describe("How this CLI was installed"),
	upgradeCommand: z
		.string()
		.optional()
		.describe("Run this shell command to install the available update"),
});
export type UpgradeOutput = z.infer<typeof upgradeOutput>;

type CtaCommand = { command: string; description?: string | undefined };
type UpgradeCtx = {
	options: { check?: boolean | undefined; background?: boolean | undefined };
	ok: (
		data: UpgradeOutput,
		meta?: {
			cta?:
				| { description?: string | undefined; commands: CtaCommand[] }
				| undefined;
		},
	) => never;
	error: (options: {
		code: string;
		message: string;
		retryable?: boolean | undefined;
	}) => never;
};

async function runBackground(env: NodeJS.ProcessEnv): Promise<void> {
	const manifest = await fetchManifest(env);
	const cache = readUpdateCache(env);
	writeUpdateCache(
		{
			...cache,
			lastCheckedAt: new Date().toISOString(),
			latestVersion: manifest.version,
		},
		env,
	);
	if (
		detectChannel(env) === "standalone" &&
		env.WHOP_CLI_AUTO_UPDATE !== "0" &&
		isNewerVersion(manifest.version, pkg.version)
	) {
		await downloadAndSwap(assetForThisBinary(manifest));
		writeUpdateCache(
			{
				lastCheckedAt: new Date().toISOString(),
				latestVersion: manifest.version,
				updatedTo: manifest.version,
			},
			env,
		);
	}
}

export async function runUpgrade(
	c: UpgradeCtx,
	env: NodeJS.ProcessEnv = process.env,
): Promise<never> {
	if (c.options.background) {
		try {
			await runBackground(env);
		} catch {
			// silent by design — the next foreground run retries after the interval
		}
		return c.ok({
			status: "up-to-date",
			current: pkg.version,
			latest: pkg.version,
			channel: detectChannel(env),
		});
	}

	const channel = detectChannel(env);

	let manifest;
	try {
		manifest = await fetchManifest(env);
	} catch (err) {
		if (err instanceof SelfUpdateError) {
			return c.error({ code: err.code, message: err.message, retryable: true });
		}
		throw err;
	}

	writeUpdateCache(
		{
			...readUpdateCache(env),
			lastCheckedAt: new Date().toISOString(),
			latestVersion: manifest.version,
		},
		env,
	);

	const base = { current: pkg.version, latest: manifest.version, channel };

	if (!isNewerVersion(manifest.version, pkg.version)) {
		return c.ok({ status: "up-to-date", ...base });
	}

	if (channel === "npm" || channel === "brew") {
		return c.ok({
			status: "update-available",
			...base,
			upgradeCommand: upgradeCommandFor(channel),
		});
	}

	if (channel === "dev") {
		return c.error({
			code: "DEV_BUILD",
			message: "Running from source — pull the latest instead of upgrading.",
			retryable: false,
		});
	}

	if (c.options.check) {
		return c.ok(
			{
				status: "update-available",
				...base,
				upgradeCommand: upgradeCommandFor(channel),
			},
			{
				cta: {
					description: `v${manifest.version} is available (you have v${pkg.version}). To update:`,
					commands: [
						{ command: "upgrade", description: "Install the latest version" },
					],
				},
			},
		);
	}

	try {
		await downloadAndSwap(assetForThisBinary(manifest));
	} catch (err) {
		if (err instanceof SelfUpdateError) {
			return c.error({
				code: err.code,
				message: err.message,
				retryable: err.code !== "PERMISSION_DENIED",
			});
		}
		throw err;
	}

	return c.ok({ status: "updated", ...base });
}
